import { useCallback, useRef, useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { QUERY_KEYS } from '@/constants';
import { fetchScans } from '@/features/scans/api';
// 24 ≈ one screenful at the denser grid (up to 6 cols), so a screen ≈ a page.
export const SCANS_PAGE_SIZE = 24;
// Next page is `page + 1` while more pages remain, else undefined (end of list).
// Exported standalone so it can be unit-tested without a query client.
export function getNextScanPageParam(lastPage) {
    const { page, totalPages } = lastPage.meta;
    return page < totalPages ? page + 1 : undefined;
}
export function useScansInfinite() {
    return useInfiniteQuery({
        queryKey: [...QUERY_KEYS.scans, 'all', { limit: SCANS_PAGE_SIZE }],
        queryFn: ({ pageParam }) => fetchScans({ page: pageParam, limit: SCANS_PAGE_SIZE }),
        initialPageParam: 1,
        getNextPageParam: getNextScanPageParam,
    });
}
// One generous page covers a whole study (~42 scans for 21 days) so the journey
// needs no pagination. Revisit if a user can accrue far more than one page.
export const USER_SCANS_LIMIT = 200;
// Fetch one user's scans for the journey drawer. Disabled until a user is
// selected so a single hook instance can serve the whole grid (open/close just
// flips `userId`). `select` filters by `scan.user` as a client-side safety net:
// a no-op if the server already filters on `userId`, self-correcting if it does
// not (see design.md "Data: filter by `userId`, with a client-side fallback").
export function useUserScans(userId) {
    return useQuery({
        queryKey: [...QUERY_KEYS.scans, 'user', userId, { limit: USER_SCANS_LIMIT }],
        queryFn: () => fetchScans({ page: 1, limit: USER_SCANS_LIMIT, userId: userId }),
        enabled: userId != null,
        select: (envelope) => envelope.data.filter((scan) => scan.user === userId),
    });
}
// Generous page size for the all-scans export: keeps the round-trip count low
// for the expected small datasets while staying correct for larger ones (the
// loop runs until `totalPages` regardless). Matches USER_SCANS_LIMIT.
export const EXPORT_PAGE_SIZE = 200;
// Retrieve EVERY scan across all pages for the grid export, then hand the full
// array to `onComplete`. Complete-or-nothing (spec: never a silently truncated
// file): `totalDocs`/`totalPages` are read from the first page's `meta`; the
// loop pages to `totalPages`; if any page throws or the accumulated count !=
// `totalDocs`, `error` is set and `onComplete` is never called.
export function useExportAllScans(onComplete) {
    const [isExporting, setIsExporting] = useState(false);
    const [error, setError] = useState(null);
    // Ref guard (not the state flag) so a rapid second click is ignored even
    // before React has re-rendered with isExporting = true.
    const runningRef = useRef(false);
    const exportAll = useCallback(async () => {
        if (runningRef.current)
            return;
        runningRef.current = true;
        setIsExporting(true);
        setError(null);
        try {
            const first = await fetchScans({ page: 1, limit: EXPORT_PAGE_SIZE });
            const { totalDocs, totalPages } = first.meta;
            const all = [...first.data];
            for (let page = 2; page <= totalPages; page++) {
                const next = await fetchScans({ page, limit: EXPORT_PAGE_SIZE });
                all.push(...next.data);
            }
            // Completeness guard: refuse to download a short file.
            if (all.length !== totalDocs) {
                throw new Error(`Export incomplete: retrieved ${all.length} of ${totalDocs} scans.`);
            }
            onComplete(all);
        }
        catch (err) {
            setError(err instanceof Error ? err.message : 'Export failed. Please try again.');
        }
        finally {
            runningRef.current = false;
            setIsExporting(false);
        }
    }, [onComplete]);
    return { exportAll, isExporting, error };
}
